'use client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BoardColumn, BoardContainer } from './board-column';
import {
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  useSensor,
  useSensors,
  KeyboardSensor,
  Announcements,
  UniqueIdentifier,
  TouchSensor,
  MouseSensor,
} from '@dnd-kit/core';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import { type Task, TaskCard } from './task-card';
import type { Column } from './board-column';
import { coordinateGetter } from './multipleContainersKeyboardPreset';
import { hasDraggableData } from './utils';
import { Button } from '@ui/components/ui/button';
import { Plus } from 'lucide-react';

export const initialKanbanCols = [
  {
    id: 'col-1' as const,
    title: 'Todo',
  },
] satisfies Column[];

export type ColumnId = (typeof initialKanbanCols)[number]['id'];

interface Props {
  onDeleteItem: (task: Task | null) => void;
  onInputChange: (id: UniqueIdentifier, value: string) => void;
  onTasksChange: (tasks: Task[]) => void;
  defaultTasks: Task[];
}
// This board exposes no "add/remove column" UI and BoardColumn's own useSortable
// call never spreads `listeners`/`attributes` onto a DOM node, so a column can never
// actually be picked up by a user, and `initialKanbanCols` is always exactly one
// column. Every Column-typed branch below, every cross-column branch (a task can
// never find itself over a task from a *different* column with only one column to
// have), and every `hasDraggableData(...)` false guard (useSortable's `data` option
// always populates it here) is dead given the current, single-fixed-column UI; not
// exercised by tests for that reason.
export const KanbanBoard = (props: Props) => {
  const [columns, setColumns] = useState<Column[]>(initialKanbanCols);
  const pickedUpTaskColumn = useRef<ColumnId | null>(null);
  const columnsId = useMemo(() => columns.map((col) => col.id), [columns]);
  const [activeColumn, setActiveColumn] = useState<Column | null>(null);
  const [tasks, setTasks] = useState<Task[]>(props.defaultTasks);
  const [activeTask, setActiveTask] = useState<Task | null>(null);

  useEffect(() => {
    setTasks(props.defaultTasks);
  }, [props.defaultTasks]);

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: coordinateGetter,
    }),
  );

  function getDraggingTaskData(taskId: UniqueIdentifier, columnId: ColumnId) {
    const tasksInColumn = tasks.filter((task) => task.columnId === columnId);
    const taskPosition = tasksInColumn.findIndex((task) => task.id === taskId);
    const column = columns.find((col) => col.id === columnId);
    return {
      tasksInColumn,
      taskPosition,
      column,
    };
  }

  const announcements: Announcements = {
    onDragStart({ active }) {
      if (!hasDraggableData(active)) return;
      if (active.data.current?.type === 'Column') {
        const startColumnIdx = columnsId.findIndex((id) => id === active.id);
        const startColumn = columns[startColumnIdx];
        return `Picked up Column ${startColumn?.title} at position: ${startColumnIdx + 1} of ${
          columnsId.length
        }`;
      } else if (active.data.current?.type === 'Task') {
        pickedUpTaskColumn.current = active.data.current.task.columnId;
        const { tasksInColumn, taskPosition, column } = getDraggingTaskData(
          active.id,
          pickedUpTaskColumn.current,
        );
        return `Picked up Task ${active.data.current.task.content} at position: ${
          taskPosition + 1
        } of ${tasksInColumn.length} in column ${column?.title}`;
      }
    },
    onDragOver({ active, over }) {
      if (!hasDraggableData(active) || !hasDraggableData(over)) return;

      if (active.data.current?.type === 'Column' && over.data.current?.type === 'Column') {
        const overColumnIdx = columnsId.findIndex((id) => id === over.id);
        return `Column ${active.data.current.column.title} was moved over ${
          over.data.current.column.title
        } at position ${overColumnIdx + 1} of ${columnsId.length}`;
      } else if (active.data.current?.type === 'Task' && over.data.current?.type === 'Task') {
        const { tasksInColumn, taskPosition, column } = getDraggingTaskData(
          over.id,
          over.data.current.task.columnId,
        );
        if (over.data.current.task.columnId !== pickedUpTaskColumn.current) {
          return `Task ${active.data.current.task.content} was moved over column ${
            column?.title
          } in position ${taskPosition + 1} of ${tasksInColumn.length}`;
        }
        return `Task was moved over position ${taskPosition + 1} of ${
          tasksInColumn.length
        } in column ${column?.title}`;
      }
    },
    onDragEnd({ active, over }) {
      if (!hasDraggableData(active) || !hasDraggableData(over)) {
        pickedUpTaskColumn.current = null;
        return;
      }
      if (active.data.current?.type === 'Column' && over.data.current?.type === 'Column') {
        const overColumnPosition = columnsId.findIndex((id) => id === over.id);

        return `Column ${active.data.current.column.title} was dropped into position ${
          overColumnPosition + 1
        } of ${columnsId.length}`;
      } else if (active.data.current?.type === 'Task' && over.data.current?.type === 'Task') {
        const { tasksInColumn, taskPosition, column } = getDraggingTaskData(
          over.id,
          over.data.current.task.columnId,
        );
        if (over.data.current.task.columnId !== pickedUpTaskColumn.current) {
          return `Task was dropped into column ${column?.title} in position ${
            taskPosition + 1
          } of ${tasksInColumn.length}`;
        }
        return `Task was dropped into position ${taskPosition + 1} of ${
          tasksInColumn.length
        } in column ${column?.title}`;
      }
      pickedUpTaskColumn.current = null;
    },
    onDragCancel({ active }) {
      pickedUpTaskColumn.current = null;
      if (!hasDraggableData(active)) return;
      return `Dragging ${active.data.current?.type} cancelled.`;
    },
  };

  function onDragStart(event: DragStartEvent) {
    if (!hasDraggableData(event.active)) return;
    const data = event.active.data.current;
    if (data?.type === 'Column') {
      setActiveColumn(data.column);
      return;
    }

    if (data?.type === 'Task') {
      setActiveTask(data.task);
      return;
    }
  }

  function onDragEnd(event: DragEndEvent) {
    setActiveColumn(null);
    setActiveTask(null);

    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (!hasDraggableData(active)) return;

    const activeData = active.data.current;

    if (activeId === overId) return;

    const isActiveAColumn = activeData?.type === 'Column';
    if (!isActiveAColumn) return;

    setColumns((columns) => {
      const activeColumnIndex = columns.findIndex((col) => col.id === activeId);

      const overColumnIndex = columns.findIndex((col) => col.id === overId);

      return arrayMove(columns, activeColumnIndex, overColumnIndex);
    });
  }

  function onDragOver(event: DragOverEvent) {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    if (!hasDraggableData(active) || !hasDraggableData(over)) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    const isActiveATask = activeData?.type === 'Task';
    const isOverATask = overData?.type === 'Task';

    if (!isActiveATask) return;

    if (isActiveATask && isOverATask) {
      setTasks((tasks) => {
        const activeIndex = tasks.findIndex((t) => t.id === activeId);
        const overIndex = tasks.findIndex((t) => t.id === overId);
        const activeTask = tasks[activeIndex];
        const overTask = tasks[overIndex];
        if (activeTask && overTask && activeTask.columnId !== overTask.columnId) {
          activeTask.columnId = overTask.columnId;
          const finalMove = arrayMove(tasks, activeIndex, overIndex - 1);
          props.onTasksChange(finalMove);
          return finalMove;
        }

        const finalMove = arrayMove(tasks, activeIndex, overIndex);
        props.onTasksChange(finalMove);
        return finalMove;
      });
    }

    const isOverAColumn = overData?.type === 'Column';

    if (isActiveATask && isOverAColumn) {
      setTasks((tasks) => {
        const activeIndex = tasks.findIndex((t) => t.id === activeId);
        const activeTask = tasks[activeIndex];
        if (activeTask) {
          activeTask.columnId = overId as ColumnId;
          const finalMove = arrayMove(tasks, activeIndex, activeIndex);
          props.onTasksChange(finalMove);
          return arrayMove(tasks, activeIndex, activeIndex);
        }
        return tasks;
      });
    }
  }

  const onTaskInputChange = (taskId: UniqueIdentifier, newValue: string) => {
    props.onInputChange(taskId, newValue);
    setTasks((state) => {
      const updatedTasks = state.map((task) => {
        if (task.id === taskId) {
          return {
            ...task,
            content: newValue,
          };
        }
        return task;
      });
      props.onTasksChange(updatedTasks);
      return updatedTasks;
    });
  };

  const addItem = () => {
    setTasks((tasks) => {
      const updatedTasks = [
        ...tasks,
        {
          id: tasks.length.toString(),
          columnId: columnsId[0],
          content: '',
        } as Task,
      ];
      props.onTasksChange(updatedTasks);
      return updatedTasks;
    });
  };

  const onDeleteItem = (task: Task | null) => {
    if (task) {
      props.onDeleteItem(task);
      setTasks((state) => state.filter((t) => t.id !== task.id));
    }
  };

  return (
    <DndContext
      accessibility={{ announcements }}
      sensors={sensors}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}>
      <BoardContainer>
        <div className="w-full">
          <SortableContext items={columnsId}>
            {columns.map((col) => (
              <BoardColumn
                key={col.id}
                column={col}
                tasks={tasks.filter((task) => task.columnId === col.id)}
                onDeleteItem={onDeleteItem}
                onInputChange={onTaskInputChange}
              />
            ))}
          </SortableContext>

          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={addItem}
            className="text-primary bg-primaryLight pl-2">
            <Plus className="text-primary" />
            Add
          </Button>
        </div>
      </BoardContainer>

      {typeof document !== 'undefined' &&
        createPortal(
          <DragOverlay>
            {activeColumn && (
              <BoardColumn
                isOverlay
                column={activeColumn}
                tasks={tasks.filter((task) => task.columnId === activeColumn.id)}
                onDeleteItem={onDeleteItem}
                onInputChange={onTaskInputChange}
              />
            )}
            {activeTask && (
              <TaskCard
                onInputChange={onTaskInputChange}
                onDeleteItem={() => onDeleteItem(activeTask)}
                task={activeTask}
                isOverlay
              />
            )}
          </DragOverlay>,
          document.body,
        )}
    </DndContext>
  );
};
